import { Divider, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useDispatch } from 'react-redux';

import { useDeleteTotpCredentialMutation, useResetPasswordMutation } from '@/api/keycloak';
import { calizaApi, useCreateTotpCredentialMutation } from '@/api/v1';
import Dot from '@/assets/icons/dot.svg?react';
import { TabPaper } from '@/components/atoms/TabPaper';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { Disable2FAOTPModal } from '@/components/molecules/Disable2FAOTPModal';
import { Enable2FAModal } from '@/components/molecules/Enable2FAModal';
import { LinkLikeButton } from '@/components/molecules/LinkLikeButton';
import { ResetPasswordModal } from '@/components/molecules/ResetPasswordModal';
import { CreateTotpRequest } from '@/DTOs/totp';
import { useCustomer } from '@/hooks/useCustomer';
import { useToast } from '@/hooks/useToast';
import { useTOTP } from '@/hooks/useTOTP';
import { useTotpSubmitHandler } from '@/hooks/useTotpSubmitHandler';
import { useTranslator } from '@/hooks/useTranslator';
import { isFetchError } from '@/utility/reactToolkit';

enum Modals {
  none = 'none',
  confirmDisableTOTP = 'confirmDisableTOTP',
  disableTOTPWithPasswordAndOTP = 'disableTOTPWithPasswordAndOTP',
  enableTOTP = 'enableTOTP',
  resetPassword = 'resetPassword',
}

export const SettingsLoginSecurityTab: React.FC = React.memo(
  function SettingsLoginSecurityTab(): React.ReactElement {
    const customer = useCustomer();

    const totp = useTOTP({
      accountName: customer.email,
    });

    const dispatch = useDispatch();
    const tr = useTranslator();
    const toast = useToast();

    const [modal, setModal] = React.useState<Modals>(Modals.none);
    const [deleteTotpCredential] = useDeleteTotpCredentialMutation();
    const [createTotpCredential] = useCreateTotpCredentialMutation();
    const [resetPassword] = useResetPasswordMutation();

    const [otpError, setOtpError] = React.useState<string | undefined>(undefined);
    const [resetPasswordLoading, setResetPasswordLoading] = React.useState<boolean>(false);

    const openEnableTOTPModal = React.useCallback((): void => {
      setModal(Modals.enableTOTP);
    }, []);

    const closeEnableTOTPModal = React.useCallback((): void => {
      setModal(Modals.none);
    }, []);

    const openConfirmDisableModal = React.useCallback((): void => {
      setModal(Modals.confirmDisableTOTP);
    }, []);

    const closeConfirmDisableModal = React.useCallback((): void => {
      setModal(Modals.none);
    }, []);

    const openDisableTOTPModal = React.useCallback((): void => {
      setModal(Modals.disableTOTPWithPasswordAndOTP);
      setOtpError(undefined);
    }, []);

    const closeDisableTotpModal = React.useCallback((): void => {
      setModal(Modals.none);
      setOtpError(undefined);
    }, []);

    const voidPreferencesCache = React.useCallback((): void => {
      const { util } = calizaApi;
      dispatch(util.invalidateTags(['UserProfile']));
    }, [dispatch]);

    const totpSubmitHandler = useTotpSubmitHandler(totp);

    const handleDisableTotpConfirm = React.useCallback(
      async (code: string): Promise<void> => {
        try {
          setOtpError(undefined);
          await deleteTotpCredential({ code: code }).unwrap();
          toast.show(tr('TOTP_DISABLED_SUCCESS'), 'success');
          closeDisableTotpModal();
        } catch (error: any) {
          if (isFetchError(error)) {
            const {
              // FIXME: why do we depend on this? :sad
              data: { error: message },
              status,
            } = error;

            if (message.includes('invalid') || status === 403) {
              setOtpError(tr('INVALID_OTP'));
            } else {
              setOtpError(tr('TOTP_DISABLED_ERROR'));
            }
          } else {
            setOtpError(tr('TOTP_DISABLED_ERROR'));
          }
        } finally {
          voidPreferencesCache();
        }
      },
      [deleteTotpCredential, toast, tr, closeDisableTotpModal, voidPreferencesCache],
    );

    const handleTotpCodeChange = React.useCallback((): void => {
      setOtpError(undefined);
    }, []);

    const openResetPasswordModal = React.useCallback((): void => {
      setModal(Modals.resetPassword);
      toast.remove();
    }, [toast]);

    const closeResetPasswordModal = React.useCallback((): void => {
      setModal(Modals.none);
      toast.remove();
    }, [toast]);

    const handleResetPasswordConfirm = React.useCallback(
      async (oldPassword: string, newPassword: string): Promise<void> => {
        setResetPasswordLoading(true);
        toast.remove();

        try {
          await resetPassword({
            oldPassword: oldPassword,
            newPassword: newPassword,
          }).unwrap();

          toast.show(tr('PASSWORD_RESET_SUCCESS'), 'success');
          closeResetPasswordModal();
        } catch (error: any) {
          if (isFetchError(error)) {
            if (error.status === 403) {
              toast.show(tr('RESET_PASSWORD_FORBIDDEN'), 'error');
              return;
            }
            toast.show(tr('RESET_PASSWORD_FAILED'), 'error');
          } else {
            toast.show(tr('RESET_PASSWORD_FAILED'), 'error');
          }
        } finally {
          setResetPasswordLoading(false);
        }
      },
      [resetPassword, toast, tr, closeResetPasswordModal],
    );

    const onTotpError = React.useCallback((error: string | undefined): void => {
      setOtpError(error);
    }, []);

    const onTotpSubmit = React.useCallback(
      async (request: CreateTotpRequest): Promise<void> => {
        try {
          await createTotpCredential(request).unwrap();

          toast.show(tr('TOTP_ENABLED_SUCCESS'), 'success');
        } catch {
          toast.show(tr('TOTP_ENABLE_FAILED'), 'error');
        } finally {
          closeEnableTOTPModal();
          voidPreferencesCache();
        }
      },
      [closeEnableTOTPModal, createTotpCredential, toast, tr, voidPreferencesCache],
    );

    return (
      <TabPaper>
        <Box display="flex" flexDirection="column" gap={5}>
          <Box sx={styles.grid}>
            <Typography variant="body2" color="custom.LightGray">
              {tr('EMAIL')}
            </Typography>
            <Box display="flex" alignItems="center" gap={3}>
              <Typography variant="body2">{customer.email}</Typography>
            </Box>
            {customer.passwordUpdatable && (
              <>
                <Typography variant="body2" color="custom.LightGray">
                  {tr('PASSWORD')}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {dots}
                  </Box>
                  <Button variant="light" size="compact" onClick={openResetPasswordModal}>
                    {tr('RESET')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" flexDirection="column" gap={1} maxWidth={514}>
              <Typography variant="h7">{tr('TWO_FACTOR_AUTHENTICATION_SECTION_LABEL')}</Typography>
              <Typography variant="body2" color="custom.LightGray">
                {tr('TWO_FACTOR_AUTHENTICATION_SECTION_DESCRIPTION')}
              </Typography>
            </Box>
            {customer.totpEnabled ? (
              <Button variant="light" size="compact" onClick={openConfirmDisableModal}>
                {tr('DISABLE')}
              </Button>
            ) : (
              <Button variant="dark" size="compact" onClick={openEnableTOTPModal}>
                {tr('ENABLE')}
              </Button>
            )}
          </Box>

          {/* Enable 2FA modal */}
          <Enable2FAModal
            open={modal === Modals.enableTOTP}
            totp={totp}
            error={otpError}
            cancelButton={<LinkLikeButton label={tr('CANCEL')} onClick={closeEnableTOTPModal} />}
            onSubmit={totpSubmitHandler(onTotpSubmit, onTotpError)}
            onCodeChange={handleTotpCodeChange}
          />

          {/* Disable 2FA confirmation modal */}
          <ConfirmationModal
            open={modal === Modals.confirmDisableTOTP}
            title={tr('DISABLE_TWO_FACTOR_AUTHENTICATION_TITLE')}
            confirmButtonText={tr('DISABLE')}
            abortButtonText={tr('CANCEL')}
            loading={false}
            onConfirm={openDisableTOTPModal}
            onAbort={closeConfirmDisableModal}
          />

          {/* Disable TOTP double verification modal */}
          <Disable2FAOTPModal
            open={modal === Modals.disableTOTPWithPasswordAndOTP}
            error={otpError}
            onConfirm={handleDisableTotpConfirm}
            onAbort={closeDisableTotpModal}
          />

          {/* Reset password modal */}
          <ResetPasswordModal
            open={modal === Modals.resetPassword}
            loading={resetPasswordLoading}
            onConfirm={handleResetPasswordConfirm}
            onClose={closeResetPasswordModal}
          />
        </Box>
      </TabPaper>
    );
  },
  (): boolean => false,
);

const styles: Record<string, SystemStyleObject> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '116px 1fr',
    alignItems: 'center',
    rowGap: 3,
  },
};

const dots = new Array(7)
  .fill(0)
  .map(
    (_: number, index: number): React.ReactElement => (
      <Dot fill="currentColor" key={index} width={12} />
    ),
  );
